/**
 * Number and GPS coordinate field formatters
 */

// ==================== GPS Coordinate Formatters ====================

/**
 * Creates a display formatter for GPS coordinates
 * @param {string} unit - Unit to display (e.g., '°', 'm')
 */
export function createGpsDisplayFormatter(unit = '') {
  return (value) => value != null ? value.toFixed(6) + unit : '-';
}

/**
 * Creates EXIF formatters for GPS coordinates
 * @param {string} positiveRef - Reference for positive values (e.g., 'N', 'E')
 * @param {string} negativeRef - Reference for negative values (e.g., 'S', 'W')
 */
export function createGpsExifFormatters(positiveRef, negativeRef) {
  return {
    formatForExif: (value) => {
      if (value === null || value === undefined) return null;
      const absValue = Math.abs(value);
      return {
        dms: decimalToDMS(absValue),
        ref: value >= 0 ? positiveRef : negativeRef
      };
    },
    formatFromExif: (dms, ref) => {
      if (!dms) return null;
      const decimal = dmsToDecimal(dms);
      return ref === negativeRef ? -decimal : decimal;
    }
  };
}

// ==================== Fraction Number Formatters ====================

/**
 * Creates formatters for numbers stored as fractions in EXIF (e.g., aperture, focal length)
 * @param {number} multiplier - Multiplier for the fraction (default 10)
 * @param {string} suffix - Display suffix (e.g., 'mm')
 */
export function createFractionFormatters(multiplier = 10, suffix = '') {
  return {
    formatForDisplay: (value) => value != null ? `${value}${suffix}` : '-',
    formatForExif: (value) => {
      if (value === null || value === undefined) return null;
      return [Math.round(value * multiplier), multiplier];
    },
    formatFromExif: (value) => {
      if (!value) return null;
      if (Array.isArray(value)) return value[0] / value[1];
      return value;
    }
  };
}

// ==================== Simple Number Formatters ====================

/**
 * Creates formatters for simple numeric display with prefix/suffix
 * @param {Object} options - Display options
 */
export function createNumberFormatters(options = {}) {
  const { prefix = '', suffix = '', formatFn = null } = options;
  return {
    formatForDisplay: (value) => {
      if (value == null) return '-';
      const formatted = formatFn ? formatFn(value) : value;
      return `${prefix}${formatted}${suffix}`;
    },
    formatForExif: (value) => value !== null ? parseFloat(value) : null,
    formatFromExif: (value) => {
      if (Array.isArray(value)) return value[0];
      return value;
    }
  };
}

// ==================== Helper Functions ====================

function dmsToDecimal(dms) {
  if (!dms || dms.length !== 3) return null;
  const degrees = dms[0][0] / dms[0][1];
  const minutes = dms[1][0] / dms[1][1];
  const seconds = dms[2][0] / dms[2][1];
  return degrees + minutes / 60 + seconds / 3600;
}

function decimalToDMS(decimal) {
  const absolute = Math.abs(decimal);
  const degrees = Math.floor(absolute);
  const minutesNotTruncated = (absolute - degrees) * 60;
  const minutes = Math.floor(minutesNotTruncated);
  const seconds = (minutesNotTruncated - minutes) * 60;

  return [[degrees, 1], [minutes, 1], [Math.round(seconds * 100), 100]];
}

// Re-export helper functions for use in other modules
export { dmsToDecimal, decimalToDMS };
